// Frontend models for the Scheduling/Booking module. Booking equals the backend BookingDTO.

export enum BookingStatus {
  OPPORTUNITY = 'opportunity',
  REQUESTED = 'requested',
  AWAITING_REPLY = 'awaiting_reply',
  CONFIRMED = 'confirmed',
  CANCELLED = 'cancelled',
}

export enum MeetingMode {
  VIRTUAL = 'virtual',
  IN_PERSON = 'in_person',
}

export enum LocationKind {
  FIXED = 'fixed',
  ACCOUNT_DEFAULT = 'account_default',
  USER_DEFAULT = 'user_default',
}

export interface LocationRef {
  kind: LocationKind
  id?: string | null
}

export enum DeliveryMode {
  SEND = 'send',
  DRAFT = 'draft',
}

export enum ResourceRefType {
  ROOM = 'room',
  SESSION = 'session',
  EMAIL = 'email',
}

export interface ResourceRef {
  type: ResourceRefType
  ids: Record<string, string>
}

export interface BookingClient {
  contactId?: string
  firstName?: string
  lastName?: string
  email?: string
  timeZone?: string
  inviteUrl?: string
}

export interface BookingInvitee {
  userId?: string
  contactId?: string
  firstName?: string
  lastName?: string
  email: string
  role?: string
}

export interface ReminderInfo {
  automated?: boolean
  count?: number
  lastSentAt?: string
  nextSendAt?: string
}

export interface Booking {
  bookingId: string
  status: BookingStatus
  title?: string
  eventTypeId?: string
  userId: string
  clients: BookingClient[]
  additionalInvitees?: BookingInvitee[]
  bookedBy?: BookingClient
  source?: ResourceRef
  start?: string
  end?: string
  invitationSent?: boolean
  reminders?: ReminderInfo
  rescheduledBy?: string
  originalStart?: string
  clientMessage?: string
  room?: ResourceRef
  session?: ResourceRef
  meetingMode?: MeetingMode
  location?: LocationRef | null
  meetingUrl?: string
  manageUrl?: string
  rescheduleUrl?: string
  cancelUrl?: string
  createdAt: string
  updatedAt: string
}

export enum EventTypeAccess {
  ACCOUNT = 'account',
  PERSONAL = 'personal',
}

export enum DayOfWeek {
  MONDAY = 'monday',
  TUESDAY = 'tuesday',
  WEDNESDAY = 'wednesday',
  THURSDAY = 'thursday',
  FRIDAY = 'friday',
  SATURDAY = 'saturday',
  SUNDAY = 'sunday',
}

export interface TimeInterval {
  start: string
  end: string
}

export interface DayAvailability {
  day: DayOfWeek
  intervals: TimeInterval[]
}

export interface AvailabilitySchedule {
  days: DayAvailability[]
}

export interface SchedulingSlot {
  start: string
  end: string
  userId?: string
}

export interface Location {
  id: string
  name: string
  address: string
  note?: string
  timeZone?: string
  ownerUserId?: string
  updatedAt: string
}

export enum AvailabilityMode {
  ACCOUNT_DEFAULT = 'account_default',
  CUSTOM = 'custom',
}

export interface EventTypeReminder {
  offsetMinutes: number
  emailTemplateId: string
}

export enum BookingNotificationTrigger {
  BOOKING_MADE = 'booking_made',
  BOOKING_CANCELLED = 'booking_cancelled',
  BOOKING_RESCHEDULED = 'booking_rescheduled',
}

export interface EventTypeNotification {
  trigger: BookingNotificationTrigger
  email: string
  alsoNotify?: string
}

export enum EmailTemplatePurpose {
  BOOKING_CONFIRMATION = 'booking_confirmation',
  RESCHEDULE_CONFIRMATION = 'reschedule_confirmation',
  CANCELLATION_NOTICE = 'cancellation_notice',
  INVITATION_TO_BOOK = 'invitation_to_book',
  RESCHEDULE_LINK = 'reschedule_link',
  MEETING_INVITATION = 'meeting_invitation',
  RESCHEDULED_INVITATION = 'rescheduled_invitation',
  CANCELLATION = 'cancellation',
}

export interface EventTypeEmailTemplate {
  purpose: EmailTemplatePurpose
  emailTemplateId: string
}

export interface EmailTemplate {
  id: string
  name: string
  purpose: EmailTemplatePurpose
  subject?: string
  body?: string
  updatedAt: string
}

export interface SchedulingEventType {
  id: string
  slug: string
  name: string
  description?: string
  durationMinutes: number
  clientFacingText?: string
  connectedMeetingTypeId?: string
  active: boolean
  access: EventTypeAccess
  addToUserPagesByDefault: boolean
  meetingMode: MeetingMode
  location?: LocationRef | null
  availabilityMode: AvailabilityMode
  availabilitySchedule?: AvailabilitySchedule
  bufferBeforeMinutes?: number
  bufferAfterMinutes?: number
  minimumNoticeMinutes?: number
  bookingWindowDays?: number
  emailTemplates: EventTypeEmailTemplate[]
  reminders: EventTypeReminder[]
  notifications: EventTypeNotification[]
  usedByCount: number
  updatedAt: string
}

export interface SchedulingUser {
  id: string
  slug?: string
  name?: string
  email?: string
  timeZone?: string
  avatarUrl?: string
  publicUrl?: string
}

export enum ApprovalMode {
  ALWAYS_REQUIRE = 'always',
  NEVER_REQUIRE = 'never',
  USERS_DECIDE = 'users_decide',
}

export enum AutomaticInvitesMode {
  OFF = 'off',
  ON = 'on',
  USERS_DECIDE = 'users_decide',
}

export interface SchedulingBranding {
  brandColor?: string
}

export interface PublicBookingPage {
  accountSlug?: string
  branding?: SchedulingBranding
  showProfilePhotos?: boolean
  showAccountName?: boolean
}

export interface UserControls {
  allowCreateEventTypes?: boolean
  allowCustomizeEventTypes?: boolean
  allowManageOwnLocations?: boolean | null
}

export interface ApprovalSettings {
  booking?: ApprovalMode
  reschedule?: ApprovalMode
}

export interface Automations {
  automaticInvites?: AutomaticInvitesMode
}

export interface AccountDefaults {
  meetingMode?: MeetingMode
  locationId?: string
  availabilitySchedule?: AvailabilitySchedule
}

export interface Compliance {
  disclosureText?: string
}

export interface AccountSchedulingSettings {
  publicBookingPage?: PublicBookingPage
  userControls?: UserControls
  approval?: ApprovalSettings
  automations?: Automations
  defaults?: AccountDefaults
  compliance?: Compliance
}

export enum CalendarProvider {
  GOOGLE = 'google',
  OUTLOOK = 'outlook',
  EXCHANGE = 'exchange',
}

export enum MeetingPlatformProvider {
  ZOOM = 'zoom',
  GOOGLE_MEET = 'google_meet',
  MS_TEAMS = 'ms_teams',
}

export interface UserBookingPage {
  slug?: string
  showProfilePhoto?: boolean
}

export enum SchedulingIntegrationState {
  LIVE = 'live',
  INVALID = 'invalid',
  VALIDATING = 'validating',
}

export interface CalendarIntegration {
  provider: CalendarProvider
  connected: boolean
  state?: SchedulingIntegrationState
  capable: boolean
}

export interface MeetingPlatformIntegration {
  provider: MeetingPlatformProvider
  connected: boolean
  state?: SchedulingIntegrationState
  capable: boolean
}

export interface UserIntegrations {
  calendars?: CalendarIntegration[]
  meetingPlatforms?: MeetingPlatformIntegration[]
  selectedCalendar?: CalendarProvider
  selectedMeetingPlatform?: MeetingPlatformProvider
}

export interface UserDefaults {
  meetingMode?: MeetingMode
  locationId?: string
  availabilitySchedule?: AvailabilitySchedule
}

export interface UserApproval {
  booking?: ApprovalMode
  reschedule?: ApprovalMode
}

export interface UserAutomations {
  automaticInvites?: AutomaticInvitesMode
}

export interface FollowUp {
  sendReminder?: boolean
  reminderOffsetMinutes?: number
  reminderEmailTemplateId?: string
  markAbandonedEnabled?: boolean
  markAbandonedAfterMinutes?: number
  abandonedAction?: string
}

export interface UserBookingRules {
  approval?: UserApproval
  automations?: UserAutomations
  publicBookingLinksEnabled?: boolean
  followUp?: FollowUp
}

export interface UserSchedulingSettings {
  bookingPage?: UserBookingPage
  integrations?: UserIntegrations
  defaults?: UserDefaults
  bookingRules?: UserBookingRules
}

export interface SchedulingFilters {
  status?: BookingStatus
  userIds?: string[]
  contactIds?: string[]
  eventTypeIds?: string[]
  search?: string
  dateFrom?: Date
  dateTo?: Date
}
